import React, { JSX, useState } from 'react';
import {
  Field,
  RichText,
} from '@sitecore-jss/sitecore-jss-nextjs';
import Texts from '../../../assets/utils/texts';
import {
  Certification,
  Training,
} from 'components/types/ProviderTypes';
import Video from 'components/atoms/Video';
import Icons from 'components/atoms/Icons';

type ProviderBioProps = {
  biographySectionId?: string;
  backgroundSectionId?: string;
  background?: Field<string>;
  specialties?: Array<string>;
  gender?: string;
  spokenLanguages?: string;
  certifications?: Array<Certification>;
  trainings?: Array<Training>;
  bio?: Field<string>;
  videoUrl?: string;
};

type ProviderInfoAccordionKey =
  | 'education'
  | 'qualifications'
  | 'languages'
  | 'gender';

const getVideoId = (videoUrl: string): string => {
  try {
    const url = new URL(videoUrl);

    if (url.hostname.includes('youtu.be')) {
      return (
        url.pathname.split('/').filter(Boolean)[0] || ''
      );
    }

    if (
      url.hostname.includes('youtube.com') ||
      url.hostname.includes('youtube-nocookie.com')
    ) {
      if (url.pathname.includes('/embed/')) {
        return (
          url.pathname
            .split('/embed/')[1]
            ?.split('/')[0] || ''
        );
      }

      if (url.pathname.includes('/shorts/')) {
        return (
          url.pathname
            .split('/shorts/')[1]
            ?.split('/')[0] || ''
        );
      }

      return url.searchParams.get('v') || '';
    }
  } catch {
    return '';
  }

  return '';
};

const ProviderBio = (
  props: ProviderBioProps
): JSX.Element => {
  const {
    biographySectionId = 'provider-biography',
    backgroundSectionId = 'provider-background',
    background,
    gender,
    spokenLanguages,
    certifications,
    trainings,
    bio,
    specialties,
    videoUrl,
  } = props;

  const [activeKey, setActiveKey] = useState<
    ProviderInfoAccordionKey | ''
  >('');

  const hasSpecialties = Boolean(
    specialties?.length
  );

  const hasCertifications = Boolean(
    certifications?.length
  );

  const hasTrainings = Boolean(trainings?.length);
  const hasGender = Boolean(gender?.trim());
  const hasLanguages = Boolean(
    spokenLanguages?.trim()
  );

  /*
   * PersonalInterests is passed through the background prop,
   * but it is displayed inside the Biography section.
   */
  const hasPersonalInterests = Boolean(
    background?.value?.trim()
  );

  const hasBio = Boolean(bio?.value?.trim());

  const normalizedVideoUrl = videoUrl?.trim() || '';
  const hasVideo = normalizedVideoUrl.length > 0;

  const videoId = hasVideo
    ? getVideoId(normalizedVideoUrl)
    : '';

  const hasBiographyInformation =
    hasBio || hasPersonalInterests || hasVideo;

  const hasQualifications =
    hasSpecialties || hasCertifications;

  const hasBackgroundInformation =
    hasTrainings ||
    hasQualifications ||
    hasLanguages ||
    hasGender;

  const toggleAccordion = (
    key: ProviderInfoAccordionKey
  ): void => {
    setActiveKey(currentKey =>
      currentKey === key ? '' : key
    );
  };

  return (
    <div className="provider-biography">
      <div className="bg-wrap">
        {hasBiographyInformation && (
          <section
            id={biographySectionId}
            className="provider-biography-section provider-anchor-target"
            aria-labelledby="provider-biography-heading"
          >
            <h2 id="provider-biography-heading">
              {Texts.Biography}
            </h2>

            {hasBio && bio && (
              <div className="biography-text">
                <RichText field={bio} />
              </div>
            )}

            {hasPersonalInterests && background && (
              <div className="paragraph-intro">
                <RichText field={background} />
              </div>
            )}

            {hasVideo &&
              (videoId ? (
                <Video
                  videoId={videoId}
                  aditionalClass="provider-video"
                  additionalId="provider-video"
                />
              ) : (
                <Video
                  videoUrl={normalizedVideoUrl}
                  aditionalClass="provider-video"
                  additionalId="provider-video"
                />
              ))}
          </section>
        )}

        {hasBackgroundInformation && (
          <section
            id={backgroundSectionId}
            className="provider-background-section provider-anchor-target"
            aria-labelledby="provider-background-heading"
          >
            <h2
              id="provider-background-heading"
              className="background-heading"
            >
              {Texts.Background}
            </h2>

            <div className="provider-info-accordion">
              {hasTrainings && (
                <div
                  className={`provider-info-accordion-item ${
                    activeKey === 'education'
                      ? 'is-active'
                      : ''
                  }`}
                >
                  <button
                    id="provider-education-header"
                    className="provider-info-accordion-header"
                    type="button"
                    aria-expanded={
                      activeKey === 'education'
                    }
                    aria-controls="provider-education-content"
                    onClick={() =>
                      toggleAccordion('education')
                    }
                  >
                    <span className="provider-info-accordion-label osf-display-h3">
                      {Texts.EducationAndExperience}
                    </span>

                    <Icons
                      id="chevron-down"
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    id="provider-education-content"
                    className="provider-info-accordion-content-wrapper"
                    role="region"
                    aria-labelledby="provider-education-header"
                    aria-hidden={
                      activeKey !== 'education'
                    }
                  >
                    <div className="provider-info-accordion-content osf-body-copy">
                      <ul className="provider-info-list">
                        {trainings?.map(
                          (training, index) => (
                            <li
                              key={`training-${index}`}
                            >
                              {training?.TrainingName
                                ?.value && (
                                <p className="provider-info-subtitle">
                                  {
                                    training
                                      .TrainingName
                                      .value
                                  }
                                </p>
                              )}

                              <p className="provider-info-detail">
                                {
                                  training
                                    ?.TrainingType
                                    ?.value
                                }

                                {training
                                  ?.FieldOfStudy
                                  ?.value &&
                                  `, ${training.FieldOfStudy.value}`}

                                {training
                                  ?.GraduationYear
                                  ?.value &&
                                  `, ${training.GraduationYear.value}`}
                              </p>
                            </li>
                          )
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}

              {hasQualifications && (
                <div
                  className={`provider-info-accordion-item ${
                    activeKey === 'qualifications'
                      ? 'is-active'
                      : ''
                  }`}
                >
                  <button
                    id="provider-qualifications-header"
                    className="provider-info-accordion-header"
                    type="button"
                    aria-expanded={
                      activeKey === 'qualifications'
                    }
                    aria-controls="provider-qualifications-content"
                    onClick={() =>
                      toggleAccordion(
                        'qualifications'
                      )
                    }
                  >
                    <span className="provider-info-accordion-label osf-display-h3">
                      {Texts.BoardCertifications}
                    </span>

                    <Icons
                      id="chevron-down"
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    id="provider-qualifications-content"
                    className="provider-info-accordion-content-wrapper"
                    role="region"
                    aria-labelledby="provider-qualifications-header"
                    aria-hidden={
                      activeKey !== 'qualifications'
                    }
                  >
                    <div className="provider-info-accordion-content osf-body-copy">
                      {hasCertifications && (
                        <div className="provider-info-section">
                          <ul className="provider-info-list">
                            {certifications?.map(
                              (
                                certification,
                                index
                              ) => {
                                const boardName =
                                  certification
                                    ?.BoardName
                                    ?.value;

                                const specialtyName =
                                  certification
                                    ?.SpecialtyName
                                    ?.value;

                                const yearCertified =
                                  certification
                                    ?.YearCertified
                                    ?.value;

                                return (
                                  <li
                                    key={`certification-${index}`}
                                  >
                                    {specialtyName && (
                                      <p className="provider-info-subtitle">
                                        {
                                          specialtyName
                                        }
                                      </p>
                                    )}

                                    <p className="provider-info-detail">
                                      {boardName}

                                      {boardName &&
                                        !yearCertified &&
                                        ' - qualified'}

                                      {yearCertified &&
                                        `${
                                          boardName
                                            ? ', '
                                            : ''
                                        }${yearCertified}`}
                                    </p>
                                  </li>
                                );
                              }
                            )}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {hasLanguages && (
                <div
                  className={`provider-info-accordion-item ${
                    activeKey === 'languages'
                      ? 'is-active'
                      : ''
                  }`}
                >
                  <button
                    id="provider-languages-header"
                    className="provider-info-accordion-header"
                    type="button"
                    aria-expanded={
                      activeKey === 'languages'
                    }
                    aria-controls="provider-languages-content"
                    onClick={() =>
                      toggleAccordion('languages')
                    }
                  >
                    <span className="provider-info-accordion-label osf-display-h3">
                      {Texts.Languages}
                    </span>

                    <Icons
                      id="chevron-down"
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    id="provider-languages-content"
                    className="provider-info-accordion-content-wrapper"
                    role="region"
                    aria-labelledby="provider-languages-header"
                    aria-hidden={
                      activeKey !== 'languages'
                    }
                  >
                    <div className="provider-info-accordion-content osf-body-copy">
                      <p className="provider-info-detail">
                        {spokenLanguages}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {hasGender && (
                <div
                  className={`provider-info-accordion-item ${
                    activeKey === 'gender'
                      ? 'is-active'
                      : ''
                  }`}
                >
                  <button
                    id="provider-gender-header"
                    className="provider-info-accordion-header"
                    type="button"
                    aria-expanded={
                      activeKey === 'gender'
                    }
                    aria-controls="provider-gender-content"
                    onClick={() =>
                      toggleAccordion('gender')
                    }
                  >
                    <span className="provider-info-accordion-label osf-display-h3">
                      {Texts.Gender}
                    </span>

                    <Icons
                      id="chevron-down"
                      aria-hidden="true"
                    />
                  </button>

                  <div
                    id="provider-gender-content"
                    className="provider-info-accordion-content-wrapper"
                    role="region"
                    aria-labelledby="provider-gender-header"
                    aria-hidden={
                      activeKey !== 'gender'
                    }
                  >
                    <div className="provider-info-accordion-content osf-body-copy">
                      <p className="provider-info-detail">
                        {gender}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ProviderBio;